/**
 * Thumbnail timing for the Automatic AD / Scene Description pipelines.
 *
 * Each scene (the span between two shot changes) gets roughly one frame every
 * two seconds, from 1 to 10 frames. The first and last frames sit three frames
 * inside the scene so a slightly-off shot change never puts a thumbnail on the
 * neighbouring shot. Example at 12 fps (offset 0.25s), a 10s scene:
 *   0.25, 2.25, 4.25, 6.25, 8.25, 9.75
 */

export const FRAME_OFFSET_FRAMES = 3;
export const FRAME_INTERVAL_SECONDS = 2;
export const MAX_FRAMES_PER_SCENE = 10;

const EPS = 1e-6;

/**
 * Split [0, duration] into scenes at each shot change time.
 * @param {Array<number|{time: number}>} shots Segment-relative shot change times
 * @param {number} duration Segment duration in seconds
 * @returns {Array<{start: number, end: number}>}
 */
export function buildScenes(shots, duration) {
    const total = Math.max(0, Number(duration) || 0);
    const cuts = [...new Set((shots || [])
        .map(s => (typeof s === "number" ? s : s && s.time))
        .filter(t => Number.isFinite(t) && t > EPS && t < total - EPS))]
        .sort((a, b) => a - b);

    const bounds = [0, ...cuts, total];
    const scenes = [];
    for (let i = 0; i < bounds.length - 1; i++) {
        if (bounds[i + 1] - bounds[i] > EPS) {
            scenes.push({ start: bounds[i], end: bounds[i + 1] });
        }
    }
    return scenes;
}

/**
 * Frame times (seconds) for one scene.
 */
export function computeSceneFrameTimes(start, end, frameRate) {
    const duration = end - start;
    if (!(duration > EPS)) return [];

    const fps = Number(frameRate) > 0 ? Number(frameRate) : 29.97;
    const offset = FRAME_OFFSET_FRAMES / fps;

    // Too short to offset both ends: one frame in the middle.
    if (duration <= 2 * offset + EPS) {
        return [start + duration / 2];
    }

    const first = start + offset;
    const last = end - offset;

    // Shorter than one interval: just the two offset frames.
    if (duration < FRAME_INTERVAL_SECONDS) {
        return [first, last];
    }

    const times = [];
    for (let t = first; t < last - EPS; t += FRAME_INTERVAL_SECONDS) {
        // Skip an interval frame that would land almost on top of the last frame.
        if (last - t < FRAME_INTERVAL_SECONDS / 2 && times.length > 0) break;
        times.push(t);
    }
    times.push(last);

    if (times.length <= MAX_FRAMES_PER_SCENE) return times;

    // Long scene: spread the maximum number of frames evenly between the offsets.
    const step = (last - first) / (MAX_FRAMES_PER_SCENE - 1);
    return Array.from({ length: MAX_FRAMES_PER_SCENE }, (_, i) => first + i * step);
}

/**
 * Frame times (seconds, rounded to centiseconds) for every scene in a segment.
 * @returns {number[]} Sorted, de-duplicated, segment-relative times
 */
export function computeShotFrameTimes(shots, duration, frameRate) {
    const total = Math.max(0, Number(duration) || 0);
    const times = buildScenes(shots, total)
        .flatMap(scene => computeSceneFrameTimes(scene.start, scene.end, frameRate))
        .map(t => Math.round(t * 100) / 100)
        .filter(t => t >= 0 && t <= total);
    return [...new Set(times)].sort((a, b) => a - b);
}

/**
 * File name for a frame, in the "<seconds>-<centiseconds>s.jpeg" form the cloud
 * functions parse (e.g. 42.05s -> "42-05s.jpeg").
 */
export function frameFileName(seconds) {
    const centis = Math.round(Math.max(0, seconds) * 100);
    const whole = Math.floor(centis / 100);
    const frac = centis % 100;
    return `${whole}-${String(frac).padStart(2, "0")}s.jpeg`;
}
